import LandingOrb from '@/components/landing/LandingOrb'
import { cn } from '@/lib/cn'
import { usePersona } from '@/lib/personas'
import { LANDING_PERSONAS } from '@/hooks/useLandingPersona'

// =============================================================================
// JarvisOrb — dashboard wrapper around the LandingOrb canvas engine.
// The orb you see signed in is the same symbol you see on the landing page.
// Design reference: docs/25_ORB_DESIGN.md
// =============================================================================

export type OrbState = 'idle' | 'listening' | 'thinking' | 'speaking'

interface JarvisOrbProps {
  state?: OrbState
  size?: 'sm' | 'md' | 'lg'
  audioLevel?: number
  className?: string
}

const dimensions: Record<NonNullable<JarvisOrbProps['size']>, number> = {
  sm: 140,
  md: 220,
  lg: 360,
}

// State → intensity multiplier on node opacity + connection strands.
const stateIntensity: Record<OrbState, number> = {
  idle: 0.85,
  listening: 1.0,
  thinking: 1.15,
  speaking: 1.3,
}

const statePulse: Record<OrbState, string> = {
  idle: 'animate-orb-pulse-idle',
  listening: 'animate-orb-pulse-listening',
  thinking: 'animate-orb-pulse-thinking',
  speaking: 'animate-orb-pulse-speaking',
}

const stateDuration: Record<OrbState, number> = {
  idle: 4000,
  listening: 2000,
  thinking: 1000,
  speaking: 500,
}

export function JarvisOrb({
  state = 'idle',
  size = 'md',
  audioLevel = 0,
  className,
}: JarvisOrbProps) {
  const { personaId } = usePersona()
  const dim = dimensions[size]
  const persona = LANDING_PERSONAS[personaId]

  // Speaking state nudges intensity up by audio level so it visibly reacts.
  const intensity = Math.min(
    1.4,
    stateIntensity[state] + (state === 'speaking' ? audioLevel * 0.3 : 0),
  )

  return (
    <div
      className={cn(
        'relative inline-block',
        statePulse[state],
        className,
      )}
      style={{
        width: dim,
        height: dim,
        animationDuration: `${stateDuration[state]}ms`,
      }}
    >
      <LandingOrb
        personaId={persona.id}
        primary={persona.primary}
        secondary={persona.secondary}
        glow={persona.glow}
        shape={persona.orbShape}
        radiusScale={persona.orbRadiusScale}
        rings={persona.rings}
        nodeCount={persona.nodeCount}
        size={dim}
        intensity={intensity}
        interaction={persona.interaction}
        interactive
      />
    </div>
  )
}
